import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DraftsIcon from '@mui/icons-material/Drafts';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface Customer {
  id: number;
  name: string;
  vat?: string;
  property_payment_term_id?: [number, string] | false;
  doc_type_name?: string;
  doc_type_code?: string;
  doc_type_short?: string;
  comprobante_type?: string;
}

interface PaymentTerm {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  list_price: number;
  qty_available: number;
  virtual_available: number;
  uom_id: [number, string];
  uom_po_id: [number, string];
  available_uoms: Array<{ id: number; name: string; factor: number; factor_inv: number; uom_type: string; price: number }>;
  taxes_id: number[]; // Impuestos de venta por defecto del producto
  discount?: number; // Descuento por defecto del producto (en porcentaje)
}

interface AffectationReason {
  code: string;
  name: string;
}

interface OrderLine {
  product_id: number;
  product_name: string;
  quantity: number;
  price_unit: number; // Precio por unidad base (siempre el precio base)
  base_price: number; // Precio base del producto
  price_per_uom: number; // Precio convertido según la UoM seleccionada
  discount: number; // Descuento en porcentaje (0-100)
  discount_amount: number; // Descuento en monto
  affectation_reason: string | null; // Código de tipo de afectación (10, 20, 30, etc.)
  uom_id: number;
  uom_name: string;
  virtual_available: number;
  available_uoms: Array<{ id: number; name: string; factor: number; factor_inv: number; uom_type: string; price: number }>;
}

const SaleOrderFormSimple: React.FC = () => {
  const { token } = useAuth();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [affectationReasons, setAffectationReasons] = useState<AffectationReason[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState<PaymentTerm | null>(null);
  const [orderLines, setOrderLines] = useState<OrderLine[]>([
    { product_id: 0, product_name: '', quantity: 1, price_unit: 0, base_price: 0, price_per_uom: 0, discount: 0, discount_amount: 0, affectation_reason: null, uom_id: 0, uom_name: '', virtual_available: 0, available_uoms: [] },
  ]);
  const [dateOrder, setDateOrder] = useState(() => {
    // Establecer fecha actual por defecto (usar componentes locales para evitar desfase por UTC)
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'draft' | 'confirm'>('draft');
  const [showStockErrorModal, setShowStockErrorModal] = useState(false);
  const [stockErrorDetails, setStockErrorDetails] = useState<Array<{
    product: string;
    requested: number;
    available: number;
    requested_uom?: string;
    requested_base?: number;
  }>>([]);
  const isEditMode = !!orderId;

  useEffect(() => {
    fetchCustomers();
    fetchProducts();
    fetchPaymentTerms();
    fetchAffectationReasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-seleccionar el plazo de pago cuando se selecciona un cliente
  useEffect(() => {
    if (selectedCustomer && paymentTerms.length > 0) {
      if (selectedCustomer.property_payment_term_id && Array.isArray(selectedCustomer.property_payment_term_id)) {
        const paymentTermId = selectedCustomer.property_payment_term_id[0];
        const paymentTerm = paymentTerms.find(pt => pt.id === paymentTermId);
        if (paymentTerm) {
          setSelectedPaymentTerm(paymentTerm);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomer, paymentTerms]);

  useEffect(() => {
    if (orderId && customers.length > 0 && products.length > 0 && paymentTerms.length > 0) {
      loadOrderData(parseInt(orderId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, customers, products, paymentTerms]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/odoo/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomers(response.data.customers || []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/odoo/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data.products || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchPaymentTerms = async () => {
    try {
      const response = await axios.get(`${API_URL}/odoo/payment_terms`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentTerms(response.data.payment_terms || []);
    } catch (err) {
      console.error('Error fetching payment terms:', err);
    }
  };

  const fetchAffectationReasons = async () => {
    try {
      const response = await axios.get(`${API_URL}/odoo/affectation_reasons`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAffectationReasons(response.data.affectation_reasons || []);
    } catch (err) {
      console.error('Error fetching affectation reasons:', err);
    }
  };


  const loadOrderData = async (id: number) => {
    setLoadingOrder(true);
    try {
      const response = await axios.get(`${API_URL}/odoo/sale_order/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const order = response.data.order;

      // Cargar cliente
      const customer = customers.find(c => c.id === order.partner_id);
      if (customer) {
        setSelectedCustomer(customer);
      }

      // Cargar plazo de pago
      if (order.payment_term_id) {
        const paymentTerm = paymentTerms.find(pt => pt.id === order.payment_term_id);
        if (paymentTerm) {
          setSelectedPaymentTerm(paymentTerm);
        }
      }

      // Cargar fecha del pedido
      if (order.date_order) {
        setDateOrder(order.date_order.split(' ')[0]); // Extraer solo la fecha (sin hora)
      }

      // Cargar líneas de pedido
      if (order.order_lines && order.order_lines.length > 0) {
        const loadedLines = order.order_lines.map((line: any) => {
          const product = products.find(p => p.id === line.product_id);
          return {
            product_id: line.product_id,
            product_name: line.product_name,
            quantity: line.product_qty,
            price_unit: line.price_unit,
            base_price: line.price_unit,
            price_per_uom: line.price_unit, // Al cargar, asumir que es el mismo
            discount: line.discount || 0,
            discount_amount: 0,
            affectation_reason: line.affectation_reason || '10', // Cargar tipo de afectación
            uom_id: line.product_uom,
            uom_name: line.uom_name,
            virtual_available: product?.virtual_available || 0,
            available_uoms: product?.available_uoms || []
          };
        });
        setOrderLines(loadedLines);
      }

      setLoadingOrder(false);
    } catch (err: any) {
      console.error('Error loading order data:', err);
      setError(err.response?.data?.detail || 'Error al cargar el pedido');
      setLoadingOrder(false);
    }
  };

  const handleAddLine = () => {
    setOrderLines([
      ...orderLines,
      { product_id: 0, product_name: '', quantity: 1, price_unit: 0, base_price: 0, price_per_uom: 0, discount: 0, discount_amount: 0, affectation_reason: null, uom_id: 0, uom_name: '', virtual_available: 0, available_uoms: [] },
    ]);
  };

  const handleRemoveLine = (index: number) => {
    if (orderLines.length > 1) {
      setOrderLines(orderLines.filter((_, i) => i !== index));
    }
  };

  const handleLineProductChange = async (index: number, productId: number) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      const newLines = [...orderLines];
      newLines[index].product_id = product.id;
      newLines[index].product_name = product.name;
      newLines[index].base_price = product.list_price; // Guardar precio base
      newLines[index].price_unit = product.list_price; // Mostrar siempre el precio base
      newLines[index].discount = product.discount || 0; // Descuento por defecto del producto
      // Tipo de afectación por defecto: '10' (Gravado - Operación Onerosa)
      newLines[index].affectation_reason = '10';

      // Por defecto usar la primera UoM disponible (embalaje si existe, sino la UoM principal)
      newLines[index].available_uoms = product.available_uoms || [];
      const firstUom = product.available_uoms?.[0];

      if (firstUom) {
        // Usar la primera UoM (embalaje si existe)
        newLines[index].uom_id = firstUom.id;
        newLines[index].uom_name = firstUom.name;
        newLines[index].price_per_uom = firstUom.price || product.list_price;
      } else {
        // Fallback a la UoM principal si no hay available_uoms
        const defaultUomId = product.uom_id?.[0] || 0;
        const defaultUomName = product.uom_id?.[1] || '';
        newLines[index].uom_id = defaultUomId;
        newLines[index].uom_name = defaultUomName;
        newLines[index].price_per_uom = product.list_price;
      }

      newLines[index].virtual_available = product.virtual_available || 0;

      // Calcular discount_amount con precio inicial
      const initialSubtotal = newLines[index].quantity * newLines[index].price_per_uom;
      newLines[index].discount_amount = (initialSubtotal * newLines[index].discount) / 100;

      // Actualizar estado inmediatamente para que los totales se muestren al instante
      setOrderLines([...newLines]);

      // Luego obtener el precio exacto del API en segundo plano
      if (newLines[index].uom_id > 0) {
        try {
          const priceResponse = await axios.get(`${API_URL}/odoo/product_price`, {
            params: {
              product_id: product.id,
              quantity: newLines[index].quantity,
              uom_id: newLines[index].uom_id
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (priceResponse.data && priceResponse.data.price !== undefined) {
            newLines[index].price_per_uom = priceResponse.data.price;
            // Recalcular discount_amount con el precio actualizado
            const subtotal = newLines[index].quantity * newLines[index].price_per_uom;
            newLines[index].discount_amount = (subtotal * newLines[index].discount) / 100;
            setOrderLines([...newLines]);
          }
        } catch (error) {
          console.error('Error al calcular precio:', error);
          // Si falla, mantener el precio inicial
        }
      }
    }
  };

  const handleLineFieldChange = async (index: number, field: 'quantity' | 'price_unit' | 'discount', value: number) => {
    const newLines = [...orderLines];

    // Si se está intentando cambiar el descuento
    if (field === 'discount') {
      // Verificar si la línea tiene tipo de afectación de bonificación (15)
      const affectation = newLines[index].affectation_reason;
      if (affectation && affectation === '15' && value > 0) {
        setError('No se puede aplicar descuento a bonificaciones');
        return;
      }
    }

    newLines[index][field] = value;

    // Recalcular discount_amount inmediatamente con valores actuales
    const line = newLines[index];
    newLines[index].discount_amount = (line.price_per_uom * line.quantity * line.discount) / 100;

    // Actualizar estado inmediatamente para que los totales se reflejen al instante
    setOrderLines([...newLines]);

    // Si cambia la cantidad, recalcular el price_per_uom y actualizar stock en segundo plano
    if (field === 'quantity' && newLines[index].product_id > 0 && newLines[index].uom_id > 0) {
      try {
        // Obtener precio y stock actualizados en paralelo
        const [priceResponse, stockResponse] = await Promise.all([
          axios.get(`${API_URL}/odoo/product_price`, {
            params: {
              product_id: newLines[index].product_id,
              quantity: value,
              uom_id: newLines[index].uom_id
            },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/odoo/product_stock`, {
            params: { product_id: newLines[index].product_id },
            headers: { Authorization: `Bearer ${token}` },
          })
        ]);

        let updated = false;
        if (priceResponse.data && priceResponse.data.price !== undefined) {
          newLines[index].price_per_uom = priceResponse.data.price;
          newLines[index].discount_amount = (newLines[index].price_per_uom * newLines[index].quantity * newLines[index].discount) / 100;
          updated = true;
        }
        if (stockResponse.data) {
          newLines[index].virtual_available = stockResponse.data.virtual_available || 0;
          updated = true;
        }
        if (updated) {
          setOrderLines([...newLines]);
        }
      } catch (error) {
        console.error('Error al calcular precio o actualizar stock:', error);
      }
    }
  };

  const handleLineAffectationChange = (index: number, affectationCode: string | null) => {
    const newLines = [...orderLines];

    // Verificar si se está intentando seleccionar bonificación (15) con descuento
    if (affectationCode === '15' && newLines[index].discount > 0) {
      setError('No se puede aplicar bonificación a productos con descuento');
      return;
    }

    // Validar que bonificación tenga un precio referencial > 0 (requerido por SUNAT)
    if (affectationCode === '15' && newLines[index].price_per_uom <= 0) {
      setError('No se puede aplicar bonificación a productos con precio S/ 0.00. SUNAT requiere un valor referencial mayor a 0.');
      return;
    }

    // Si se selecciona bonificación (15), resetear el descuento a 0
    if (affectationCode === '15') {
      newLines[index].discount = 0;
      newLines[index].discount_amount = 0;
    }

    newLines[index].affectation_reason = affectationCode;
    setOrderLines(newLines);
  };

  const handleLineUomChange = async (index: number, uomId: number) => {
    const newLines = [...orderLines];
    const selectedUom = newLines[index].available_uoms.find((uom) => uom.id === uomId);
    if (selectedUom) {
      newLines[index].uom_id = selectedUom.id;
      newLines[index].uom_name = selectedUom.name;

      // Recalcular price_per_uom según pricelist con la cantidad actual
      // price_unit siempre permanece como el precio base
      if (newLines[index].product_id > 0) {
        try {
          const response = await axios.get(`${API_URL}/odoo/product_price`, {
            params: {
              product_id: newLines[index].product_id,
              quantity: newLines[index].quantity,
              uom_id: uomId
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          if (response.data && response.data.price !== undefined) {
            // Actualizar price_per_uom para el cálculo del subtotal
            newLines[index].price_per_uom = response.data.price;
          }
        } catch (error) {
          console.error('Error al calcular precio:', error);
          // Si falla, usar el precio pre-calculado de la UoM
          newLines[index].price_per_uom = selectedUom.price || newLines[index].base_price;
        }
      }

      // Recalcular discount_amount basado en el nuevo precio
      const line = newLines[index];
      newLines[index].discount_amount = (line.price_per_uom * line.quantity * line.discount) / 100;

      setOrderLines(newLines);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedCustomer) {
      setError('Debe seleccionar un cliente');
      return;
    }

    const validLines = orderLines.filter((line) => line.product_id > 0);
    if (validLines.length === 0) {
      setError('Debe agregar al menos un producto');
      return;
    }

    // Verificar que ningún producto tenga stock previsto = 0
    const productsWithNoStock = validLines.filter((line) => line.virtual_available <= 0);
    if (productsWithNoStock.length > 0) {
      const productNames = productsWithNoStock.map((line) => line.product_name).join(', ');
      setError(`No se puede confirmar la orden. Los siguientes productos no tienen stock previsto: ${productNames}`);
      return;
    }

    // Validar que líneas de bonificación tengan precio referencial > 0 (requerido por SUNAT)
    const bonificacionSinPrecio = validLines.filter(
      (line) => line.affectation_reason === '15' && line.price_per_uom <= 0
    );
    if (bonificacionSinPrecio.length > 0) {
      const productNames = bonificacionSinPrecio.map((line) => line.product_name).join(', ');
      setError(`Los siguientes productos de bonificación tienen precio S/ 0.00, SUNAT requiere un valor referencial mayor a 0: ${productNames}`);
      return;
    }

    // Preparar datos del pedido para validación
    const orderData = {
      partner_id: selectedCustomer.id,
      date_order: dateOrder,
      payment_term_id: selectedPaymentTerm?.id,
      auto_confirm: false,
      order_line: validLines.map((line) => ({
        product_id: line.product_id,
        name: line.product_name,
        product_uom_qty: line.quantity,
        price_unit: line.price_per_uom,
        product_uom: line.uom_id,
        discount: line.discount,
        l10n_pe_edi_affectation_reason: line.affectation_reason,
      })),
    };

    // Validar stock con el backend (considerando unidades de medida)
    try {
      setLoading(true);
      await axios.post(
        `${API_URL}/odoo/sale_order/validate_stock`,
        orderData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      if (err.response?.status === 400 && err.response?.data?.detail) {
        const errorMessage = err.response.data.detail;
        if (errorMessage.includes('STOCK INSUFICIENTE')) {
          // Parsear el mensaje de error para extraer los detalles
          const lines = errorMessage.split('\n').filter((l: string) => l.startsWith('•'));
          const errorDetails = lines.map((line: string) => {
            // Parsear líneas como: • Producto: Solicitado X unidades, Disponible: Y unidades
            const match = line.match(/•\s*(.+?):\s*Solicitado\s+([\d.]+)\s+(.+?)\s+\(=\s+([\d.]+)\s+unidades\),\s*Disponible:\s+([\d.]+)/);
            if (match) {
              return {
                product: match[1],
                requested: parseFloat(match[2]),
                requested_uom: match[3],
                requested_base: parseFloat(match[4]),
                available: parseFloat(match[5])
              };
            }
            // Formato simple sin conversión
            const simpleMatch = line.match(/•\s*(.+?):\s*Solicitado\s+([\d.]+),\s*Disponible:\s+([\d.]+)/);
            if (simpleMatch) {
              return {
                product: simpleMatch[1],
                requested: parseFloat(simpleMatch[2]),
                available: parseFloat(simpleMatch[3])
              };
            }
            return null;
          }).filter((item: any) => item !== null);

          if (errorDetails.length > 0) {
            setStockErrorDetails(errorDetails);
            setShowStockErrorModal(true);
            return;
          }
        }
        setError(errorMessage);
        return;
      }
      setError('Error al validar stock');
      return;
    }

    // Validar restricciones de fecha (3 días antes, ningún día después)
    const orderDate = new Date(dateOrder);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    orderDate.setHours(0, 0, 0, 0);

    const minDate = new Date(today);
    minDate.setDate(today.getDate() - 3);

    if (orderDate < minDate || orderDate > today) {
      setError('La fecha del pedido solo puede ser hasta 3 días antes de hoy y ningún día después de hoy (restricción SUNAT)');
      return;
    }

    // Validar que si el total es 0, el plazo de pago sea "Contado"
    if (isFreeOrder()) {
      const isContado = selectedPaymentTerm?.name.toLowerCase().includes('contado') ||
                       selectedPaymentTerm?.name.toLowerCase().includes('inmediato') ||
                       selectedPaymentTerm?.name.toLowerCase().includes('immediate');
      if (!isContado) {
        setError('Para pedidos con total S/ 0.00, el plazo de pago debe ser "Contado" (restricción SUNAT)');
        return;
      }
    }

    // Mostrar modal de confirmación
    setShowConfirmModal(true);
  };

  const downloadQuotationPDF = async (saleOrderId: number) => {
    try {
      const response = await axios.get(
        `${API_URL}/odoo/sale_order/${saleOrderId}/quotation_pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Extraer el nombre del archivo del header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Cotizacion_${saleOrderId}.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"|filename=([^;\s]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1] || filenameMatch[2];
        }
      }

      // Crear un blob URL y descargar
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error al descargar PDF de cotización:', error);
      throw error;
    }
  };

  const handleConfirmOrder = async (action: 'draft' | 'confirm') => {
    setShowConfirmModal(false);
    setLoading(true);
    setError('');
    setSuccess('');

    const validLines = orderLines.filter((line) => line.product_id > 0);

    try {
      const payload = {
        partner_id: selectedCustomer!.id,
        order_line: validLines.map((line) => ({
          product_id: line.product_id,
          name: line.product_name,
          product_uom_qty: line.quantity,
          price_unit: line.price_per_uom, // Enviar el precio convertido según la UoM
          product_uom: line.uom_id, // El backend espera product_uom, no product_uom_id
          discount: line.discount,
          l10n_pe_edi_affectation_reason: line.affectation_reason, // Enviar el tipo de afectación
        })),
        date_order: dateOrder || undefined,
        payment_term_id: selectedPaymentTerm?.id || undefined,
        auto_confirm: action === 'confirm', // Nuevo parámetro
      };

      let response;
      if (isEditMode) {
        // Actualizar pedido existente
        response = await axios.put(`${API_URL}/odoo/sale_order/${orderId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess(`¡Pedido actualizado exitosamente!`);

        // Navegar de vuelta a la página de pedidos después de actualizar
        setTimeout(() => {
          navigate('/orders');
        }, 1500);
      } else {
        // Crear nuevo pedido
        response = await axios.post(`${API_URL}/odoo/sale_order`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (action === 'confirm') {
          setSuccess(`¡Pedido confirmado y facturado exitosamente! ID: ${response.data.sale_order_id}`);
        } else {
          // Guardar como borrador - descargar PDF de cotización automáticamente
          const saleOrderId = response.data.sale_order_id;
          setSuccess(`¡Cotización guardada! Descargando PDF...`);

          // Descargar PDF de cotización
          try {
            await downloadQuotationPDF(saleOrderId);
            setSuccess(`¡Cotización guardada y PDF descargado! ID: ${saleOrderId}`);
          } catch (pdfError: any) {
            console.error('Error descargando PDF:', pdfError);
            setSuccess(`¡Cotización guardada! ID: ${saleOrderId} (No se pudo descargar el PDF)`);
          }
        }

        // Navegar a la página de pedidos después de crear
        setTimeout(() => {
          navigate('/orders');
        }, 1500);
      }
    } catch (err: any) {
      // Manejar diferentes tipos de errores
      let errorMessage = isEditMode ? 'Error al actualizar el pedido' : 'Error al crear el pedido';

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        // Si detail es un array de objetos de validación, extraer los mensajes
        if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Calcular subtotal de una línea (después de descuento)
  const calculateLineSubtotal = (line: OrderLine) => {
    // Verificar si la línea tiene operación gratuita (código 15 = Bonificación)
    const affectation = line.affectation_reason;
    if (affectation === '15') {
      return 0; // Operaciones gratuitas no suman al subtotal
    }

    const subtotal = line.quantity * line.price_per_uom;
    const discount = subtotal * (line.discount / 100);
    return subtotal - discount;
  };

  // Calcular IGV de una línea (solo para operaciones gravadas - código 10)
  // Como los precios YA incluyen IGV, extraemos el IGV del total
  const calculateLineTax = (line: OrderLine) => {
    const affectation = line.affectation_reason;

    // Solo las operaciones gravadas (código 10 = Venta) tienen IGV (18%)
    if (affectation !== '10') {
      return 0;
    }

    // El precio ya incluye IGV, entonces: IGV = Total / 1.18 * 0.18
    const totalWithIGV = calculateLineSubtotal(line);
    const baseImponible = totalWithIGV / 1.18;
    return baseImponible * 0.18; // IGV 18%
  };

  // Calcular total de una línea (los precios YA incluyen IGV, no sumar adicional)
  const calculateLineTotal = (line: OrderLine) => {
    // Como price_per_uom ya incluye IGV, solo calculamos cantidad × precio - descuento
    return calculateLineSubtotal(line);
  };

  // Subtotal = Base imponible (sin IGV)
  const calculateSubtotal = () => {
    return orderLines.reduce((sum, line) => {
      const affectation = line.affectation_reason;
      // Operaciones gratuitas no suman al subtotal
      if (affectation === '15') {
        return sum;
      }

      // Total con IGV de esta línea
      const totalWithIGV = calculateLineSubtotal(line);
      // Extraer base imponible (sin IGV)
      const baseImponible = affectation === '10' ? totalWithIGV / 1.18 : totalWithIGV;
      return sum + baseImponible;
    }, 0);
  };

  const calculateDiscount = () => {
    return orderLines.reduce((sum, line) => {
      // Las operaciones gratuitas (código 15 = Bonificación) no tienen descuento contabilizable
      const affectation = line.affectation_reason;
      if (affectation === '15') {
        return sum;
      }

      const subtotal = line.quantity * line.price_per_uom;
      const discount = subtotal * (line.discount / 100);
      return sum + discount;
    }, 0);
  };

  const calculateIGV = () => {
    return orderLines.reduce((sum, line) => sum + calculateLineTax(line), 0);
  };

  // Total = suma de todos los totales con IGV de cada línea
  const calculateTotal = () => {
    return orderLines.reduce((sum, line) => {
      const affectation = line.affectation_reason;
      // Operaciones gratuitas no suman al total
      if (affectation === '15') {
        return sum;
      }
      return sum + calculateLineSubtotal(line);
    }, 0);
  };

  // Calcular el valor de productos gratuitos (bonificaciones)
  const calculateFreeAmount = () => {
    return orderLines.reduce((sum, line) => {
      // Verificar si la línea tiene operación gratuita (código 15 = Bonificación)
      const affectation = line.affectation_reason;
      if (affectation === '15') {
        return sum + (line.quantity * line.price_per_uom);
      }
      return sum;
    }, 0);
  };

  // Calcular rango de fechas permitidas (3 días antes, ningún día después de hoy)
  const getMinDate = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() - 3);
    return `${minDate.getFullYear()}-${String(minDate.getMonth() + 1).padStart(2, '0')}-${String(minDate.getDate()).padStart(2, '0')}`;
  };

  const getMaxDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  // Verificar si el total es 0 (pedido gratuito)
  const isFreeOrder = () => {
    return calculateTotal() === 0;
  };


  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 3 } }}>
      <Paper elevation={3} sx={{ padding: { xs: 2, sm: 3, md: 4 }, borderRadius: { xs: 0, sm: 2 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
            fontWeight: 600,
            color: 'primary.main',
            mb: 3
          }}
        >
          {isEditMode ? 'Editar Pedido de Venta' : 'Crear Pedido de Venta'}
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={{ xs: 3, sm: 4 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
              <Autocomplete
                options={customers || []}
                getOptionLabel={(option) => {
                  if (!option?.name) return '';
                  const docType = option.doc_type_short || '';
                  const vat = option.vat || '';
                  return `${option.name} - ${docType} ${vat}`;
                }}
                value={selectedCustomer}
                onChange={(_, newValue) => setSelectedCustomer(newValue)}
                filterOptions={(options, state) => {
                  // Limit results to 100 for performance
                  const filtered = options.filter(option =>
                    option.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                    option.vat?.includes(state.inputValue)
                  );
                  return filtered.slice(0, 100);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente"
                    required
                    placeholder="Buscar cliente..."
                    sx={{ bgcolor: 'background.paper' }}
                    helperText={
                      selectedCustomer?.comprobante_type
                        ? `Se emitirá: ${selectedCustomer.comprobante_type}`
                        : customers.length > 100
                          ? "Escribe para filtrar clientes"
                          : ""
                    }
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                        {option.doc_type_short && (
                          <Chip
                            label={option.doc_type_short}
                            size="small"
                            color={option.doc_type_code === '6' ? 'primary' : 'default'}
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {option.vat || 'Sin documento'}
                        </Typography>
                        {option.comprobante_type && (
                          <Chip
                            label={`→ ${option.comprobante_type}`}
                            size="small"
                            color={option.comprobante_type === 'FACTURA' ? 'success' : 'info'}
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: '20px', ml: 'auto' }}
                          />
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                noOptionsText="No se encontraron clientes"
              />

              <TextField
                fullWidth
                label="Fecha del Pedido"
                type="date"
                value={dateOrder}
                onChange={(e) => setDateOrder(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: getMinDate(),
                  max: getMaxDate()
                }}
                sx={{ bgcolor: 'background.paper' }}
                helperText="Solo se permite 3 días antes de hoy, ningún día después"
              />
            </Box>

            <Autocomplete
              options={paymentTerms || []}
              getOptionLabel={(option) => option?.name || ''}
              value={selectedPaymentTerm}
              onChange={(_, newValue) => setSelectedPaymentTerm(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Plazo de Pago"
                  placeholder="Seleccionar plazo..."
                  sx={{ bgcolor: 'background.paper' }}
                />
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              noOptionsText="No se encontraron plazos de pago"
            />

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Líneas de Pedido
            </Typography>

            {orderLines.map((line, index) => (
              <Paper
                key={index}
                elevation={1}
                sx={{
                  p: { xs: 2, sm: 3 },
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: 'info.main', fontWeight: 600 }}>
                      Línea {index + 1}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => handleRemoveLine(index)}
                      disabled={orderLines.length === 1}
                      sx={{
                        minWidth: { xs: 70, sm: 100 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '4px 8px', sm: '6px 16px' }
                      }}
                    >
                      Eliminar
                    </Button>
                  </Box>

                  <Autocomplete
                    options={products || []}
                    getOptionLabel={(option) => option?.name || ''}
                    value={products.find((p) => p.id === line.product_id) || null}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        handleLineProductChange(index, newValue.id);
                      }
                    }}
                    getOptionDisabled={(option) => option.virtual_available <= 0}
                    filterOptions={(options, state) => {
                      // Limit results to 100 for performance
                      const filtered = options.filter(option =>
                        option.name.toLowerCase().includes(state.inputValue.toLowerCase())
                      );
                      return filtered.slice(0, 100);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Producto"
                        required
                        placeholder="Buscar producto..."
                        sx={{ bgcolor: 'background.paper' }}
                        helperText={products.length > 100 ? "Escribe para filtrar productos" : ""}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="body2">
                            {option.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: option.virtual_available <= 0 ? 'error.main' : 'text.secondary'
                            }}
                          >
                            Stock: {option.virtual_available?.toFixed(2) || 0} {option.uom_id?.[1] || ''} |
                            Precio: S/ {option.list_price?.toFixed(2) || 0}
                            {option.virtual_available <= 0 && ' - SIN STOCK'}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    noOptionsText="No se encontraron productos"
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <TextField
                      label="Cantidad"
                      type="number"
                      value={line.quantity}
                      onChange={(e) => handleLineFieldChange(index, 'quantity', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 1, step: 1 }}
                      fullWidth
                      sx={{ bgcolor: 'background.paper' }}
                      helperText={line.virtual_available > 0 ? `Stock: ${line.virtual_available.toFixed(2)}` : ''}
                    />
                    <FormControl fullWidth sx={{ bgcolor: 'background.paper' }}>
                      <InputLabel>Unidad de Medida</InputLabel>
                      <Select
                        value={line.uom_id || ''}
                        onChange={(e) => handleLineUomChange(index, Number(e.target.value))}
                        label="Unidad de Medida"
                        disabled={line.available_uoms.length === 0}
                      >
                        {line.available_uoms.map((uom) => (
                          <MenuItem key={uom.id} value={uom.id}>
                            {uom.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Precio Unitario"
                      type="number"
                      value={line.price_unit}
                      InputProps={{ readOnly: true }}
                      fullWidth
                      sx={{
                        bgcolor: 'action.selected',
                        '& .MuiInputBase-input': { color: 'text.secondary' }
                      }}
                    />
                    <TextField
                      label="Descuento (%)"
                      type="number"
                      value={line.discount}
                      onChange={(e) => handleLineFieldChange(index, 'discount', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                      fullWidth
                      disabled={line.affectation_reason === '15'}
                      sx={{
                        bgcolor: line.affectation_reason === '15' ? 'action.selected' : 'background.paper'
                      }}
                      helperText={line.affectation_reason === '15' ? 'No se puede aplicar descuento a operaciones gratuitas' : ''}
                    />
                    <FormControl fullWidth sx={{ bgcolor: 'background.paper' }}>
                      <InputLabel>Tipo de Afectación</InputLabel>
                      <Select
                        value={line.affectation_reason || ''}
                        onChange={(e) => handleLineAffectationChange(index, e.target.value || null)}
                        label="Tipo de Afectación"
                      >
                        {affectationReasons.map((reason) => (
                          <MenuItem
                            key={reason.code}
                            value={reason.code}
                            disabled={reason.code === '15' && line.discount > 0}
                          >
                            {reason.name}
                            {reason.code === '15' && line.discount > 0 && ' - Deshabilitado por descuento'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Subtotal"
                      value={`S/ ${calculateLineTotal(line).toFixed(2)}`}
                      InputProps={{ readOnly: true }}
                      fullWidth
                      sx={{
                        bgcolor: 'action.selected',
                        '& .MuiInputBase-input': { fontWeight: 600, color: line.affectation_reason === '15' ? 'success.main' : 'primary.main' }
                      }}
                      helperText={line.affectation_reason === '15' ? 'Operación Gratuita' : ''}
                    />
                  </Box>
                </Stack>
              </Paper>
            ))}

            <Button
              variant="outlined"
              onClick={handleAddLine}
              fullWidth
              startIcon={<span style={{ fontSize: '1.2rem' }}>+</span>}
              sx={{
                fontSize: { xs: '0.875rem', sm: '1rem' },
                py: { xs: 1.5, sm: 2 },
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Agregar Línea de Producto
            </Button>

            <Divider sx={{ my: 2 }} />

            <Paper
              elevation={2}
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: 'background.paper',
                borderRadius: 2
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Subtotal */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'text.secondary' }}>
                    Subtotal:
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'text.secondary' }}>
                    S/ {calculateSubtotal().toFixed(2)}
                  </Typography>
                </Box>

                {/* Descuento - solo mostrar si hay descuento */}
                {calculateDiscount() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'warning.main' }}>
                      Descuento:
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'warning.main' }}>
                      - S/ {calculateDiscount().toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* Impuestos */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'text.secondary' }}>
                    Impuestos:
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'text.secondary' }}>
                    S/ {calculateIGV().toFixed(2)}
                  </Typography>
                </Box>

                {/* Gratuito - solo mostrar si hay productos con bonificación */}
                {calculateFreeAmount() > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'success.main' }}>
                      Gratuito:
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600, color: 'success.main' }}>
                      S/ {calculateFreeAmount().toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Total */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', p: 2, borderRadius: 1 }}>
                  <Typography variant="h5" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 700, color: 'white' }}>
                    Total:
                  </Typography>
                  <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' }, fontWeight: 700, color: 'white' }}>
                    S/ {calculateTotal().toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Button
              type="submit"
              variant="contained"
              color="success"
              fullWidth
              size="large"
              disabled={loading}
              sx={{
                py: { xs: 2, sm: 2.5 },
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 600,
                boxShadow: 3,
                '&:hover': { boxShadow: 6 }
              }}
            >
              {loading ? <CircularProgress size={28} color="inherit" /> : isEditMode ? 'Actualizar Pedido' : 'Crear Pedido de Venta'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* Modal de Confirmación */}
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 600, py: 2 }}>
          Confirmar Pedido de Venta
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, minHeight: '400px' }}>
            {/* Columna Izquierda - Resumen */}
            <Box sx={{ p: 3, bgcolor: 'action.hover', borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                Resumen del Pedido
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                    Cliente
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedCustomer?.name}
                  </Typography>
                </Box>

                {selectedPaymentTerm && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Plazo de Pago
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedPaymentTerm.name}
                    </Typography>
                  </Box>
                )}

                {dateOrder && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                      Fecha del Pedido
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(dateOrder).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Desglose de totales en el modal */}
                <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Subtotal:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      S/ {calculateSubtotal().toFixed(2)}
                    </Typography>
                  </Box>

                  {calculateDiscount() > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="warning.main">
                        Descuento:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                        - S/ {calculateDiscount().toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Impuestos:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      S/ {calculateIGV().toFixed(2)}
                    </Typography>
                  </Box>

                  {calculateFreeAmount() > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="success.main">
                        Gratuito:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                        S/ {calculateFreeAmount().toFixed(2)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Total:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      S/ {calculateTotal().toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            {/* Columna Derecha - Productos */}
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
                Productos ({orderLines.filter(line => line.product_id > 0).length})
              </Typography>

              <Box sx={{ maxHeight: '300px', overflowY: 'auto', pr: 1 }}>
                {orderLines.filter(line => line.product_id > 0).map((line, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 1.5,
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, mr: 2 }}>
                        {line.product_name}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                        S/ {calculateLineTotal(line).toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={`${line.quantity} ${line.uom_name}`}
                        size="small"
                        sx={{
                          borderColor: 'info.main',
                          color: 'info.main',
                          fontWeight: 600
                        }}
                        variant="outlined"
                      />
                      <Chip
                        label={`S/ ${line.price_unit.toFixed(2)} c/u`}
                        size="small"
                        sx={{
                          borderColor: 'text.secondary',
                          color: 'text.primary',
                        }}
                        variant="outlined"
                      />
                      {line.discount > 0 && (
                        <Chip
                          label={`Desc: ${line.discount}%`}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {line.affectation_reason && (
                        <Chip
                          label={`Tipo: ${affectationReasons.find(r => r.code === line.affectation_reason)?.name || line.affectation_reason}`}
                          size="small"
                          variant="outlined"
                          color={['21', '31', '32', '33', '34', '35', '36'].includes(line.affectation_reason) ? 'success' : 'primary'}
                        />
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Alert severity="info" icon={false} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Seleccione cómo desea crear el pedido:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem' }}>
                  📄 <strong>Borrador:</strong> Podrá editarlo después
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  ✅ <strong>Confirmar:</strong> Se generará automáticamente picking y factura
                </Typography>
              </Alert>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, justifyContent: 'center' }}>
          <Tooltip title="Cancelar">
            <IconButton
              onClick={() => setShowConfirmModal(false)}
              color="inherit"
              size="large"
              sx={{
                border: '2px solid',
                borderColor: 'grey.400',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <CancelIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Guardar como Borrador">
            <IconButton
              onClick={() => handleConfirmOrder('draft')}
              color="warning"
              disabled={loading}
              size="large"
              sx={{
                border: '2px solid',
                borderColor: 'warning.main',
                bgcolor: 'warning.main',
                color: 'white',
                '&:hover': { bgcolor: 'warning.dark' }
              }}
            >
              <DraftsIcon fontSize="large" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Confirmar y Facturar">
            <IconButton
              onClick={() => handleConfirmOrder('confirm')}
              color="success"
              disabled={loading}
              size="large"
              sx={{
                border: '2px solid',
                borderColor: 'success.main',
                bgcolor: 'success.main',
                color: 'white',
                '&:hover': { bgcolor: 'success.dark' }
              }}
            >
              <CheckCircleIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        </DialogActions>
      </Dialog>

      {/* Modal de Error de Stock */}
      <Dialog
        open={showStockErrorModal}
        onClose={() => setShowStockErrorModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'error.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon fontSize="large" />
          Stock Insuficiente
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            La cantidad solicitada excede el stock disponible
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Los siguientes productos no tienen suficiente stock:
          </Typography>
          <Stack spacing={2}>
            {stockErrorDetails.map((item, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: 'error.lighter',
                  borderColor: 'error.main',
                  borderWidth: 2
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.dark', mb: 1 }}>
                  {item.product}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={item.requested_uom
                      ? `Solicitado: ${item.requested} ${item.requested_uom}`
                      : `Solicitado: ${item.requested}`}
                    color="error"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  {item.requested_base && item.requested_uom && (
                    <Chip
                      label={`= ${item.requested_base} unidades`}
                      color="error"
                      variant="outlined"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  <Chip
                    label={`Disponible: ${item.available} unidades`}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`Faltante: ${(item.requested_base || item.requested) - item.available} unidades`}
                    color="default"
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowStockErrorModal(false)}
            variant="contained"
            color="primary"
            fullWidth
            size="large"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SaleOrderFormSimple;